import React from "react";
import ContributionWaterfall from "../components/ContributionWaterfall";
import { netDelta, fmtMoney, fmtSignedMoney, fmtSignedPct, fmtPct } from "../format";

function fmtRange(w) {
  if (!w?.from || !w?.to) return null;
  const opts = { day: "numeric", month: "short", year: "numeric" };
  return `${new Date(w.from).toLocaleDateString(undefined, opts)} – ${new Date(w.to).toLocaleDateString(undefined, opts)}`;
}

export default function EvidenceTab({ inv, version, drivers = [], audit }) {
  const period = inv?.scope?.period;
  const net = netDelta(drivers);
  const current = drivers.reduce((s, d) => s + (d.comparisonCost || 0), 0);
  const baseline = drivers.reduce((s, d) => s + (d.baselineCost || 0), 0);
  const pct = baseline > 0 ? (net / baseline) * 100 : 0;
  const shown = [...drivers].filter((d) => Math.abs(d.deltaCost || 0) >= 0.005).sort((a, b) => Math.abs(b.deltaCost) - Math.abs(a.deltaCost));
  const rows = [
    ["Current period spend", fmtMoney(current)],
    ["Comparison-period spend", fmtMoney(baseline)],
    ["Absolute variance", fmtSignedMoney(net)],
    ["Percentage variance", fmtSignedPct(pct)],
    ["Confidence", inv?.confidenceLabel || "—"],
  ];
  return (
    <>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-card border border-card-bd bg-card p-6">
          <div className="text-[16px] font-semibold leading-snug text-text">Net spend changed {fmtSignedMoney(net)} versus the preceding period.</div>
          <dl className="mt-4 space-y-2">
            {rows.map(([k, v]) => (
              <div key={k} className="flex items-center justify-between border-b border-card-bd pb-2 text-[13px] last:border-b-0">
                <dt className="text-text-3">{k}</dt>
                <dd className="tabular-nums font-medium text-text-2">{v}</dd>
              </div>
            ))}
          </dl>
        </div>
        <ContributionWaterfall drivers={drivers} max={net} />
      </div>

      <div className="overflow-hidden rounded-card border border-card-bd bg-card">
        <div className="flex items-center gap-4 px-5 pt-4 text-[11px] font-semibold uppercase tracking-[0.04em] text-text-3">
          <div className="flex-1">Evidence item</div>
          <div className="w-[90px] text-right">Previous</div>
          <div className="w-[90px] text-right">Current</div>
          <div className="w-[90px] text-right">Change</div>
          <div className="w-[52px] text-right">Share</div>
        </div>
        {shown.map((d) => (
          <div key={`${d.dimension}:${d.key}`} className="flex items-center gap-4 border-b border-card-bd px-5 py-2.5 text-[13px] last:border-b-0">
            <div className="flex-1 truncate text-text-2">{d.label || d.key}</div>
            <div className="w-[90px] text-right tabular-nums text-text-3">{fmtMoney(d.baselineCost)}</div>
            <div className="w-[90px] text-right tabular-nums text-text-3">{fmtMoney(d.comparisonCost)}</div>
            <div className="w-[90px] text-right tabular-nums font-semibold text-text">{fmtSignedMoney(d.deltaCost)}</div>
            <div className="w-[52px] text-right tabular-nums text-text-3">{fmtPct(d.sharePct)}</div>
          </div>
        ))}
      </div>

      <div className="text-[12px] text-text-3">
        {audit?.records ? `Based on ${audit.records.toLocaleString("en-US")} billing records` : "Based on the cost-base window"}
        {audit?.sources?.length ? ` · ${audit.sources.join(" and ")}` : ""}
        {version?.computedAt ? ` · last refreshed ${new Date(version.computedAt).toLocaleDateString()}` : ""}
        {period ? ` · ${fmtRange(period)}` : ""}
      </div>
    </>
  );
}
